/**
 * Phase 4 PR G — public case-study practice-question handler.
 *
 * Wires up:
 *   - Radio change → POST /education/casestudy/setanswers (writes IDataProtector
 *     cookie via server proxy; cookie is HttpOnly so JS can't write directly)
 *   - Submit button → POST /education/casestudy/submitonemodule
 *     Branches on the JSON response status:
 *       AuthenticationNeeded       → bounce to /?showLoginModal=1
 *       AlreadyHasExamInProgress   → bounce to /profile/dashboard
 *       HasNotStartedRegistration  → bounce to /profile/dashboard (start exam from there)
 *       LoggedInExamNotRegistered  → bounce to /education/casestudy completion CTA (TBD: shopping cart route)
 *       NotAllAnswersSelected      → inline status message
 *   - Show/Hide Case Study toggle (reuses the Exam.cshtml convention)
 *
 * Markup contract (Views/Education/CaseStudy.cshtml + CaseStudyFull.cshtml):
 *   - data-amri-case-study-form[data-exam-type][data-profession-type][data-case-study-id]
 *   - data-amri-case-study-question[data-question-id]
 *   - data-amri-case-study-answer[data-question-id][data-answer-id]
 *   - data-amri-case-study-submit                — submit button
 *   - data-amri-case-study-status                 — aria-live status div
 *   - data-amri-case-studies-toggle               — show/hide button
 *   - data-amri-case-studies-panel                — sidebar
 *   - data-amri-antiforgery-form                  — hidden token form
 *
 * window.AmriCaseStudyPageData = { examType, professionType, caseStudyId,
 *                                  setAnswersUrl, submitUrl }
 * is injected by the view before this script loads.
 */

declare global {
    interface Window {
        AmriCaseStudyPageData?: {
            examType: number;
            professionType: number | null;
            caseStudyId: number | null;
            setAnswersUrl: string;
            submitUrl: string;
        };
    }
}

interface SubmitOneModuleResponse {
    status: number;
    answers?: Array<{ id: number; isCorrect: boolean }>;
    completionPercentage?: number;
    examName?: string;
}

const STATUS_NOT_ALL_ANSWERS_SELECTED = 0;
const STATUS_AUTHENTICATION_NEEDED = 1;
const STATUS_HAS_NOT_STARTED_REGISTRATION = 2;
const STATUS_ALREADY_HAS_EXAM_IN_PROGRESS = 3;
const STATUS_LOGGED_IN_EXAM_NOT_REGISTERED = 4;

const CASE_STUDIES_HIDDEN_KEY = 'amri-education-case-study:case-studies-hidden';
const CASE_STUDIES_HIDDEN_CLASS = 'amri-education-case-study--case-studies-hidden';

function getAntiforgeryToken(): string {
    const input = document.querySelector<HTMLInputElement>(
        '[data-amri-antiforgery-form] input[name="__RequestVerificationToken"]'
    );
    return input?.value ?? '';
}

function getSelectedAnswerIds(): number[] {
    const radios = document.querySelectorAll<HTMLInputElement>(
        '[data-amri-case-study-answer]:checked'
    );
    const ids: number[] = [];
    radios.forEach((r) => {
        const value = Number.parseInt(r.value, 10);
        if (!Number.isNaN(value)) {
            ids.push(value);
        }
    });
    return ids;
}

async function postAnswersToCookie(): Promise<void> {
    const data = window.AmriCaseStudyPageData;
    if (!data) return;
    const token = getAntiforgeryToken();
    if (!token) return;
    const payload = { answerIds: getSelectedAnswerIds() };

    try {
        await fetch(data.setAnswersUrl, {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
                'Content-Type': 'application/json',
                RequestVerificationToken: token,
            },
            body: JSON.stringify(payload),
        });
    } catch {
        // Cookie write is best-effort; if it fails the user simply loses the
        // pre-fill on next page load. The Submit POST re-reads the cookie,
        // so a transient network error is recoverable on resubmit.
    }
}

function setStatusMessage(message: string, isError: boolean): void {
    const status = document.querySelector<HTMLElement>('[data-amri-case-study-status]');
    if (!status) return;
    status.textContent = message;
    status.hidden = message.length === 0;
    status.classList.toggle('amri-education-case-study--status--error', isError && message.length > 0);
}

async function submitOneModule(): Promise<void> {
    const data = window.AmriCaseStudyPageData;
    if (!data) return;
    const token = getAntiforgeryToken();
    if (!token) {
        setStatusMessage('Antiforgery token missing — please refresh the page.', true);
        return;
    }

    const form = document.querySelector<HTMLElement>('[data-amri-case-study-form]');
    const examType = form?.dataset.examType ?? String(data.examType);
    const professionType = form?.dataset.professionType ?? (data.professionType ?? '');
    const params = new URLSearchParams();
    params.append('examType', examType);
    if (professionType !== '') {
        params.append('professionType', String(professionType));
    }

    const url = `${data.submitUrl}?${params.toString()}`;
    setStatusMessage('Submitting…', false);

    let response: Response;
    try {
        response = await fetch(url, {
            method: 'POST',
            credentials: 'same-origin',
            headers: { RequestVerificationToken: token },
        });
    } catch {
        setStatusMessage('Network error — please try again.', true);
        return;
    }

    if (!response.ok) {
        setStatusMessage(`Server error (${response.status}) — please try again.`, true);
        return;
    }

    const body = (await response.json()) as SubmitOneModuleResponse;
    switch (body.status) {
        case STATUS_NOT_ALL_ANSWERS_SELECTED:
            setStatusMessage('Please answer every question before submitting.', true);
            break;
        case STATUS_AUTHENTICATION_NEEDED:
        case STATUS_LOGGED_IN_EXAM_NOT_REGISTERED:
            // Legacy parity: reveal per-radio ✓/✗ markers + the completion
            // panel with the cert/recert CTAs. Cookie still holds the
            // answers + question ids for the eventual cart-add (the
            // `data-amri-get-certified` handler routes to checkout).
            renderResults(body);
            break;
        case STATUS_HAS_NOT_STARTED_REGISTRATION:
            setStatusMessage('You have a registration ready — redirecting to your dashboard.', false);
            window.location.assign('/profile/dashboard');
            break;
        case STATUS_ALREADY_HAS_EXAM_IN_PROGRESS:
            setStatusMessage('You already have an exam in progress — redirecting…', false);
            window.location.assign('/profile/dashboard');
            break;
        default:
            setStatusMessage('Unexpected response — please refresh and try again.', true);
            break;
    }
}

function renderResults(body: SubmitOneModuleResponse): void {
    setStatusMessage('', false);

    // Mark each user-selected radio with a correctness icon. Spans were
    // pre-rendered next to every radio in the view; show only the ones
    // matching the user's selected answer IDs.
    const answers = body.answers ?? [];
    for (const ans of answers) {
        const span = document.querySelector<HTMLElement>(
            `[data-amri-answer-status][data-answer-id="${ans.id}"]`
        );
        if (!span) continue;
        span.classList.toggle('amri-education-answer-status--correct', ans.isCorrect);
        span.classList.toggle('amri-education-answer-status--incorrect', !ans.isCorrect);
        span.dataset.amriShown = '1';
    }

    // Lock the form — radios become read-only after submit.
    document.querySelectorAll<HTMLInputElement>('[data-amri-case-study-answer]').forEach((r) => {
        r.disabled = true;
    });
    const submitBtn = document.querySelector<HTMLButtonElement>('[data-amri-case-study-submit]');
    if (submitBtn) submitBtn.hidden = true;

    // Reveal the completion panel + populate the percent / exam-name slots.
    const panel = document.querySelector<HTMLElement>('[data-amri-case-study-completion]');
    if (panel) {
        panel.hidden = false;
        const percentSlot = panel.querySelector<HTMLElement>('[data-amri-case-study-completion-percent]');
        if (percentSlot && body.completionPercentage !== undefined) {
            percentSlot.textContent = String(body.completionPercentage);
        }
        const examSlot = panel.querySelector<HTMLElement>('[data-amri-case-study-completion-exam]');
        if (examSlot && body.examName) {
            examSlot.textContent = body.examName;
        }
        panel.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
}

function initCaseStudiesToggle(): void {
    const toggle = document.querySelector<HTMLButtonElement>('[data-amri-case-studies-toggle]');
    if (!toggle) return;

    const applyHidden = (hidden: boolean) => {
        document.body.classList.toggle(CASE_STUDIES_HIDDEN_CLASS, hidden);
        toggle.setAttribute('aria-pressed', hidden ? 'true' : 'false');
        const showLabel = toggle.querySelector<HTMLElement>('[data-amri-case-studies-label-show]');
        const hideLabel = toggle.querySelector<HTMLElement>('[data-amri-case-studies-label-hide]');
        if (showLabel) showLabel.hidden = !hidden;
        if (hideLabel) hideLabel.hidden = hidden;
    };

    let initialHidden = false;
    try {
        initialHidden = window.localStorage.getItem(CASE_STUDIES_HIDDEN_KEY) === '1';
    } catch {
        initialHidden = false;
    }
    applyHidden(initialHidden);

    toggle.addEventListener('click', () => {
        const nextHidden = !document.body.classList.contains(CASE_STUDIES_HIDDEN_CLASS);
        applyHidden(nextHidden);
        try {
            window.localStorage.setItem(CASE_STUDIES_HIDDEN_KEY, nextHidden ? '1' : '0');
        } catch {
            // localStorage unavailable (private mode, quota) — ignore; the
            // toggle still works for the current session.
        }
    });
}

function initAnswerCookieWrites(): void {
    document.querySelectorAll<HTMLInputElement>('[data-amri-case-study-answer]').forEach((radio) => {
        radio.addEventListener('change', () => {
            void postAnswersToCookie();
        });
    });
}

function initSubmitButton(): void {
    const submitBtn = document.querySelector<HTMLButtonElement>('[data-amri-case-study-submit]');
    if (!submitBtn) return;
    submitBtn.addEventListener('click', () => {
        void submitOneModule();
    });
}

function init(): void {
    initCaseStudiesToggle();
    initAnswerCookieWrites();
    initSubmitButton();
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
} else {
    init();
}

export {};
